<!-- An interactive SVG network for the data -->
<template>
    <div>
        <svg id="network" v-bind:height="height"></svg>
    </div>
</template>

<script>
import * as d3 from "d3";
import store from "@/store";

export default {
    name: "NetworkGraph",
    props: {
        // Whether to show suggestions or not
        showRelated: Boolean,
        // The data to be used for the nodes (and links) of the graph
        nodeData: Array,
        // Whether the user's top data is being used (or manually set data)
        topData: Boolean,
        // The current list of manual data
        manualList: Array,
        // Callback function to remove from the manual list by id
        removeFromManualList: Function,
        // Callback function to add a given node to the manual list
        addToManualList: Function,
        // Callback function to reload the data
        reloadData: Function,
        // Callback function to show the manual list popup from NetworkWidget
        showManualListPopup: Function
    },
    data: function() {
        return {
            // The node data
            nodes: [],
            // The IDs of the non-suggested nodes
            mainIds: [],
            // The link data
            links: []
        };
    },
    computed: {},
    watch: {
        // If showRelated is toggled, redraw the network
        showRelated: function() {
            this.updateSVG();
        },
        // If the data source switches to manual, draw the manual data list overlay
        topData: function() {
            if (!this.topData) {
                this.redrawListOverlay();
            }
        },
        // If the manual list changes update the overlays
        manualList: function() {
            if (!this.topData) {
                this.redrawListOverlay();
            }
            this.updateOverlaySizeAndPosition();
        },
        // If the graph data changes, update the network data, redraw the network and redraw the overlays
        nodeData: function() {
            this.updateData();
            this.selectedNode = null;
            this.createGraph();
            this.updateSVG();
            this.redrawOverlays();
        }
    },
    created() {
        this.height = this.$attrs.height ? this.$attrs.height : 600;
        this.width = this.$attrs.width ? this.$attrs.width : 1000;
        this.updateData();
    },
    mounted() {
        this.svg = d3.select("svg");
        this.height = this.$el.clientHeight;
        this.width = this.$el.clientWidth;

        this.createGraph();
        this.redrawOverlays();
    },
    methods: {
        // Reconstruct the graph from the data
        updateData() {
            const artists = this.nodeData;
            // Construct the nodes
            this.nodes = artists.map(a => {
                const mainList = a.list_index != null && a.list_index !== -1;
                return {
                    id: a.identity,
                    name: a.name,
                    imgSrc: a.image_url,
                    connections: a.related ? a.related : [],
                    listIndex: a.list_index,
                    mainList: mainList,
                    colour: mainList ? "#01Bf01" : "#017301",
                    popularity: a.popularity,
                    score: a.score,
                    radius: (a.score / 100) * 20 + 2,
                    fontSize: (a.score / 100) * 10 + 5,
                    ...a
                };
            });
            this.maxFontSize = 20;
            this.mainIds = this.nodes.filter(n => n.mainList).map(n => n.id);

            // Construct the links
            this.links = [];
            this.nodes.forEach(n =>
                n.connections.map(tid => {
                    const t = this.nodes.find(node => node.id === tid);
                    this.links.push({
                        source: n.id,
                        target: tid,
                        colour: "gray",
                        strokeWidth: ((n.score + t.score) / 200) * 3 + 0.5
                    });
                })
            );
        },
        // Draw the SVG components for the nodes and links data
        createGraph() {
            this.svg.selectAll("*").remove();
            this.container = this.svg.insert("g").attr("class", "everything");

            this.svg.attr("preserveAspectRatio", "xMidYMid meet");

            // Add zoom handler to svg
            d3.zoom().on("zoom", e => {
                this.container.attr("transform", e.transform);
                this.updateSVG();
            })(this.svg);

            //draw links
            this.linkContainer = this.container
                .append("g")
                .attr("class", "links")
                .selectAll("line");
            this.linkLineCollection = this.linkContainer
                .data(this.links)
                .enter()
                .append("line")
                .style("stroke", l => l.colour);
            this.updateLinkWidth();

            //draw nodes
            this.nodeContainer = this.container
                .append("g")
                .attr("class", "nodes")
                .selectAll("circle");
            this.nodeCircleCollection = this.nodeContainer
                .data(this.nodes)
                .enter()
                .append("circle")
                .attr("r", n => n.radius)
                .on("click", this.onNodeClick);
            this.updateNodeColour();

            //draw labels
            this.labelsContainer = this.container
                .append("g")
                .attr("class", "labels")
                .selectAll("text");
            this.labelTextCollection = this.labelsContainer
                .data(this.nodes)
                .enter()
                .append("text")
                .text(n => n.name)
                .attr("font-size", n => n.fontSize);
            this.updateLabelColour();

            this.setUpForces();

            window.addEventListener("resize", this.onResize);

            this.dataOverlay = this.svg.append("svg").attr("class", "overlay");
            this.listOverlay = this.svg.append("svg").attr("class", "overlay");
            this.updateOverlaySizeAndPosition();
        },
        // Resize handler
        onResize() {
            this.width = this.$el.clientWidth;
            this.height = this.$el.clientHeight;
            this.setUpForces();
            this.updateNodePositions();
            this.updateOverlaySizeAndPosition();
            this.redrawOverlays();
        },
        // Node Click handler
        onNodeClick(e, n) {
            if (this.selectedNode === n) {
                this.selectedNode = null;
            } else {
                this.selectedNode = n;
            }
            this.updateSVG();
            this.redrawDataOverlay();
        },
        // Update the visuals of the network
        updateSVG() {
            this.updateLinkWidth();
            this.updateNodeColour();
            this.updateLabelColour();
            this.updateOverlaySizeAndPosition();
        },
        // Redraw the overlays depending on the set flags
        redrawOverlays() {
            if (this.selectedNode) {
                this.redrawDataOverlay();
            }
            if (!this.topData) {
                this.redrawListOverlay();
            }
        },
        // Draw the overlay for the selected node's data
        redrawDataOverlay() {
            const n = this.selectedNode;
            const fontSize = 15;
            const topPad = 25;
            const bottomPad = 15;
            const midPad = 10;
            this.dataOverlay.selectAll("*").remove();

            if (n == null) {
                return;
            }

            // Draw overlay background
            this.dataOverlay
                .append("rect")
                .attr("rx", 10)
                .attr("fill", "#adadad")
                .attr("height", this.dataOverlay.attr("height"))
                .attr("width", this.dataOverlay.attr("width"));

            // Draw text onto overlay
            let labels = [n.name];
            if (n.artists_names) {
                labels.push("Artist: " + n.artists_names);
            }
            labels.push("Popularity: " + n.popularity + "/100");
            this.dataOverlay
                .append("g")
                .attr("class", "data-attributes")
                .selectAll("text")
                .data(labels)
                .enter()
                .append("text")
                .text(l => l)
                .attr("font-size", fontSize)
                .attr("x", fontSize)
                .attr("y", (d, i) => (fontSize + midPad) * i + topPad)
                .attr("font-weight", (d, i) => (i === 0 ? "bold" : "normal"))
                .attr("fill", "black");

            const textEnd =
                (labels.length - 1) * (fontSize + midPad) + midPad + topPad;

            // Draw 'add to list' button
            this.dataOverlay
                .append("rect")
                .attr("class", "overlayBtn")
                .attr("width", fontSize * 1.5)
                .attr("height", fontSize * 1.5)
                .attr("rx", 10)
                .attr("x", this.dataOverlay.attr("width") - fontSize * 2)
                .attr("y", topPad - fontSize)
                .attr("fill", "green")
                .on("click", () => this.addToManualList(n));
            this.dataOverlay
                .append("text")
                .text("+")
                .attr("class", "overlayBtn")
                .attr("font-size", fontSize * 1.5)
                .attr("x", this.dataOverlay.attr("width") - fontSize * 1.7)
                .attr("y", topPad * 1.15)
                .attr("font-weight", "bold")
                .attr("fill", "black")
                .on("click", () => this.addToManualList(n));

            // Draw node image
            // Image height = min(overlay width, overlay height)
            const imageHeight = Math.max(
                Math.min(
                    this.dataOverlay.attr("height") - bottomPad - textEnd,
                    this.dataOverlay.attr("width") - 2 * fontSize
                ),
                0 // Ensure width is positive even when overlay is not visible (width < padding)
            );
            this.dataOverlay
                .append("image")
                .attr("href", n.imgSrc)
                .attr("x", fontSize)
                .attr("y", textEnd)
                .attr("height", imageHeight)
                .attr("width", imageHeight);

            this.dataOverlay
                .append("text")
                .text("▶")
                .attr("class", "overlayBtn")
                .attr("font-size", fontSize * 2)
                .attr("x", this.dataOverlay.attr("width") / 2 - fontSize)
                .attr("y", this.dataOverlay.attr("height") - topPad)
                .attr("font-weight", "bold")
                .attr("fill", "white")
                .on("click", () => store.commit("play", n.uri));

            this.dataOverlay
                .append("text")
                .text("▶")
                .attr("class", "overlayBtn")
                .attr("font-size", fontSize * 1.5)
                .attr("x", this.dataOverlay.attr("width") / 2 - fontSize * 0.9)
                .attr("y", this.dataOverlay.attr("height") - topPad * 1.1)
                .attr("font-weight", "bold")
                .attr("fill", "green")
                .on("click", () => store.commit("play", n.uri));
        },
        // Draw the overlay for the manual data list
        redrawListOverlay() {
            const fontSize = 15;
            const topPad = 25;
            const bottomPad = 15;
            const midPad = 10;
            this.listOverlay.selectAll("*").remove();

            // Draw overlay background
            this.listOverlay
                .append("rect")
                .attr("rx", 10)
                .attr("fill", "#adadad")
                .attr("height", this.listOverlay.attr("height"))
                .attr("width", this.listOverlay.attr("width"));

            // Draw the list
            this.listOverlay
                .append("g")
                .attr("class", "manualList")
                .selectAll("text")
                .data(this.manualList)
                .enter()
                .append("text")
                .text(l => l.name)
                .attr("font-size", fontSize)
                .attr("x", fontSize)
                .attr("y", (d, i) => (fontSize + midPad) * (i + 1) + topPad)
                .attr("fill", "black")
                .each(function(d) {
                    d.width = this.getBoundingClientRect().width;
                });

            // Add title
            this.listOverlay
                .append("text")
                .text("Title")
                .attr("font-size", fontSize)
                .attr("x", fontSize)
                .attr("y", topPad)
                .attr("font-weight", "bold")
                .attr("fill", "black");

            // Draw delete buttons
            this.listOverlay
                .append("g")
                .attr("class", "manualListDelete")
                .selectAll("text")
                .data(this.manualList)
                .enter()
                .append("text")
                .text("X")
                .attr("class", "overlayBtn")
                .attr("font-size", fontSize)
                .attr("x", d =>
                    Math.min(
                        fontSize + 4 + d.width,
                        this.listOverlay.attr("width") - fontSize - 4
                    )
                )
                .attr("y", (d, i) => (fontSize + midPad) * (i + 1) + topPad)
                .attr("font-weight", "bold")
                .attr("fill", "red")
                .on("click", (e, d) =>
                    this.removeFromManualList(d.identity ? d.identity : d.id)
                );

            // Add append button
            this.listOverlay
                .append("rect")
                .attr("class", "overlayBtn")
                .attr("width", fontSize * 5.5)
                .attr("height", fontSize * 1.5)
                .attr("rx", 5)
                .attr("x", fontSize)
                .attr(
                    "y",
                    (fontSize + midPad) * (this.manualList.length + 1) +
                        topPad -
                        fontSize
                )
                .attr("fill", "lightBlue")
                .on("click", this.showManualListPopup);
            this.listOverlay
                .append("text")
                .text("Add data")
                .attr("class", "overlayBtn")
                .attr("font-size", fontSize)
                .attr("x", fontSize * 1.4)
                .attr(
                    "y",
                    (fontSize + midPad) * (this.manualList.length + 1) + topPad
                )
                .attr("font-weight", "bold")
                .attr("fill", "black")
                .on("click", this.showManualListPopup);

            // Add reload button
            this.listOverlay
                .append("rect")
                .attr("class", "overlayBtn")
                .attr("width", fontSize * 4.5)
                .attr("height", fontSize * 1.5)
                .attr("rx", 5)
                .attr("x", fontSize)
                .attr(
                    "y",
                    this.listOverlay.attr("height") - bottomPad - fontSize * 2
                )
                .attr("fill", "green")
                .on("click", this.reloadData);
            this.listOverlay
                .append("text")
                .text("Reload")
                .attr("class", "overlayBtn")
                .attr("font-size", fontSize)
                .attr("x", fontSize * 1.4)
                .attr(
                    "y",
                    this.listOverlay.attr("height") - bottomPad - fontSize
                )
                .attr("font-weight", "bold")
                .attr("fill", "black")
                .on("click", this.reloadData);
        },
        // Helper function to apply 2 functions depending on whether a node is a suggestion or a main node
        ifRelatedNodeDoElseDo(node, relatedFn, mainFn) {
            if (node.mainList) {
                return mainFn(node);
            }
            return relatedFn(node);
        },
        // Update the colours of the nodes
        updateNodeColour() {
            this.nodeCircleCollection.attr("fill", n =>
                this.ifRelatedNodeDoElseDo(
                    n,
                    () => (this.showRelated ? n.colour : "transparent"),
                    n => n.colour
                )
            );
            this.nodeCircleCollection.classed(
                "selected",
                n => n === this.selectedNode
            );
        },
        // Update the colours of the labels
        updateLabelColour() {
            this.labelTextCollection.attr("fill", n =>
                this.ifRelatedNodeDoElseDo(
                    n,
                    () => (this.showRelated ? "white" : "transparent"),
                    () => "white"
                )
            );
            this.labelTextCollection.attr("opacity", n =>
                this.getLabelOpacity(n)
            );
            this.labelTextCollection.classed(
                "selected",
                n => n === this.selectedNode
            );
        },
        // Helper function to determine the opacity of a label for a given node based on the current zoom level
        getLabelOpacity(node) {
            const transform = this.container.attr("transform");
            const match = /scale\((.*)\)/.exec(transform);
            let scale;
            if (match == null || (scale = match[1]) == null) {
                scale = 1.0;
            }
            const fontRatio = node.radius / this.maxFontSize;
            // Zoom scale level at which all labels are visible
            const lbScale = 1.3;
            // The minimum font size ratio (as a proportion of this.maxFontSize)
            const minRatio = 0.5;
            // Interpolated (inverse quadratic) font ratio between scale bounds
            const interpRatio =
                (lbScale / Math.sqrt(minRatio)) * Math.sqrt(1 - fontRatio);
            return (scale / interpRatio) ** 3;
        },
        // Update the width of the links
        updateLinkWidth() {
            const linkConnectsToRelated = link =>
                !(
                    this.mainIds.includes(link.source.id) &&
                    this.mainIds.includes(link.target.id)
                );

            this.linkLineCollection.attr("stroke-width", l =>
                !this.showRelated && linkConnectsToRelated(l)
                    ? 0
                    : l.strokeWidth
            );
        },
        // Update the position and sizes of the 2 overlays to fit the current network SVG size
        updateOverlaySizeAndPosition() {
            this.width = this.$el.clientWidth;
            this.height = this.$el.clientHeight;
            // Set position and size of the overlay for the selected node data
            const dataOverlayWidth = Math.min(
                Math.max(this.width / 5, 200),
                this.width / 2.01
            );
            const dataOverlayHeight = this.height / 2;
            this.dataOverlay
                .attr("x", this.width - dataOverlayWidth)
                .attr("y", this.height - dataOverlayHeight)
                .attr("height", dataOverlayHeight)
                .attr("width", dataOverlayWidth);

            // Set position and size of the manual list overlay

            // The maximum width of the labels in the manualList overlay
            const maxLabelWidth = this.manualList.length
                ? Math.max(
                      ...this.manualList.map(n => (n.width ? n.width : 0))
                  ) + 50
                : 0;
            const listOverlayWidth = Math.min(
                Math.max(dataOverlayWidth, maxLabelWidth),
                this.width / 2.01
            );
            const listOverlayHeight = dataOverlayHeight;
            this.listOverlay
                .attr("x", 0)
                .attr("y", this.height - listOverlayHeight)
                .attr("height", listOverlayHeight)
                .attr("width", listOverlayWidth);
        },
        // Initialise the forces between nodes in the network
        setUpForces() {
            const simulation = d3.forceSimulation().nodes(this.nodes);
            const link_force = d3.forceLink(this.links).id(n => n.id);
            // -ve = outward pressure on graph
            const charge_force = d3.forceManyBody().strength(-2500);

            // Translate to centre - fit to landscape or portrait
            const xStrength = this.width > this.height ? 0.3 : 0.6;
            const yStrength = this.width > this.height ? 0.6 : 0.4;
            const center_force = {
                x: d3.forceX(this.width / 2).strength(xStrength),
                y: d3.forceY(this.height / 2).strength(yStrength)
            };
            // Prevents Overlap
            const collide_force = d3
                .forceCollide()
                .radius(n => n.radius * Math.ceil(this.width / 300));
            simulation
                .force("charge_force", charge_force)
                .force("x", center_force.x)
                .force("y", center_force.y)
                .force("collide", collide_force)
                .force("links", link_force)
                .on("tick", this.updateNodePositions);
        },
        // Update the SVG element positions to match the calculated positions as a result of the forces
        updateNodePositions() {
            if (this.nodeCircleCollection) {
                this.nodeCircleCollection
                    .attr("cx", n => n.x)
                    .attr("cy", n => n.y);
            }
            this.labelTextCollection
                .attr("x", n => n.x + n.radius * 1.5)
                .attr("y", n => n.y + n.fontSize / 4);
            this.linkLineCollection
                .attr("x1", l => l.source.x)
                .attr("y1", l => l.source.y)
                .attr("x2", l => l.target.x)
                .attr("y2", l => l.target.y);
        }
    }
};
</script>

<style lang="scss">
#network {
    background-color: inherit;
    margin-left: auto;
    margin-right: auto;
    display: block;
    width: 100%;

    text {
        cursor: default;

        &.overlayBtn {
            cursor: pointer;
        }

        &.selected {
            text-decoration: underline;
            opacity: 1 !important;
        }
    }

    circle {
        cursor: pointer;

        &:hover {
            fill: honeydew;
        }

        &.selected {
            stroke: honeydew;
            stroke-width: 3px;
        }
    }
}
</style>
